import React, { useEffect, useMemo, useRef } from "react";
import { Box, TextField, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormik, FormikProvider } from "formik";
import { Mail } from "lucide-react";
import * as Yup from "yup";
import {
  ExperienceErrorText,
  ExperienceFieldGuide,
  ExperienceFieldLabel,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  type ExperienceMomentVariant,
  fieldHelperText,
  getExperienceFieldSx,
  getPrimaryButtonSx,
  isSeasonMomentVariant,
  } from "./ExperienceUI";

interface PostcardData {
  title?: string;
  description?: string;
  url?: string | File;
  signature?: string | File;
}

interface PostcardExperienceProps {
  darkMode: boolean;
  data?: PostcardData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: PostcardData) => void;
}

const EMPTY_SEASON_POSTCARD_DATA: Required<PostcardData> = {
  title: "",
  description: "",
  url: "",
  signature: "",
};

const PostcardExperience: React.FC<PostcardExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const isInternalUpdateRef = useRef(false);
  const fieldSx = getExperienceFieldSx(darkMode);
  const defaults = isSeason
    ? EMPTY_SEASON_POSTCARD_DATA
    : {
        title: t("postcard_default_title"),
        description: t("postcard_default_description"),
        url: t("postcard_default_url"),
        signature: t("postcard_default_signature"),
      };

  const validationSchema = isSeason
    ? Yup.object({
        url: Yup.mixed<string | File>()
          .required(t("postcard_image_required_season"))
          .test(
            "is-file-or-url",
            t("postcard_image_invalid"),
            (value) =>
              value instanceof File ||
              (typeof value === "string" &&
                value.trim() !== "" &&
                (/^https?:\/\/.+/i.test(value) ||
                  value.startsWith("data:") ||
                  value.startsWith("blob:"))),
          ),
        title: Yup.string().nullable(),
        description: Yup.string().nullable(),
        signature: Yup.mixed<string | File>().nullable(),
      })
    : Yup.object({
    url: Yup.mixed<string | File>()
      .required(t("postcard_image_required"))
      .test(
        "is-file-or-url",
        t("invalid_url"),
        (value) =>
          value instanceof File ||
          (typeof value === "string" &&
            (value.trim() === "" ||
              /^https?:\/\/.+/i.test(value) ||
              value.startsWith("data:") ||
              value.startsWith("blob:")))
      ),
    title: Yup.string().nullable(),
    description: Yup.string().nullable(),
    signature: Yup.mixed<string | File>().nullable(),
  });

  const formik = useFormik<PostcardData>({
    initialValues: {
      title: data?.title ?? defaults.title,
      description: data?.description ?? defaults.description,
      url: data?.url ?? defaults.url,
      signature: data?.signature ?? defaults.signature,
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: !isSeason,
    validateOnChange: false,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const next: PostcardData = {
        title: data.title ?? defaults.title,
        description: data.description ?? defaults.description,
        url: data.url ?? defaults.url,
        signature: data.signature ?? defaults.signature,
      };
      const changed = Object.keys(next).some(
        (key) =>
          formik.values[key as keyof PostcardData] !==
          next[key as keyof PostcardData]
      );
      if (changed) {
        formik.setValues(next);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (!onDataChange) return;
    const timeoutId = setTimeout(() => {
      isInternalUpdateRef.current = true;
      onDataChange({
        title: formik.values.title,
        description: formik.values.description,
        url: formik.values.url,
        signature: formik.values.signature,
      });
    }, 0);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "url" | "signature"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    formik.setFieldValue(field, file);
    formik.setFieldError(field, undefined);
    e.target.value = "";
  };

  const mainImagePreviewUrl = useMemo(() => {
    if (!formik.values.url) return "";
    if (formik.values.url instanceof File) {
      return URL.createObjectURL(formik.values.url);
    }
    return formik.values.url;
  }, [formik.values.url]);

  const signaturePreviewUrl = useMemo(() => {
    if (!formik.values.signature) return "";
    if (formik.values.signature instanceof File) {
      return URL.createObjectURL(formik.values.signature);
    }
    return formik.values.signature;
  }, [formik.values.signature]);

  useEffect(() => {
    return () => {
      if (formik.values.url instanceof File && mainImagePreviewUrl) {
        URL.revokeObjectURL(mainImagePreviewUrl);
      }
      if (formik.values.signature instanceof File && signaturePreviewUrl) {
        URL.revokeObjectURL(signaturePreviewUrl);
      }
    };
  }, [
    formik.values.url,
    formik.values.signature,
    mainImagePreviewUrl,
    signaturePreviewUrl,
  ]);

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={Mail}
            title={t("photocard")}
            subtitle={t("postcard_header_subtitle")}
          />
        )}

        <ExperienceFieldGuide darkMode={darkMode}>
          {isSeason
            ? t("postcard_field_guide_season")
            : t("photocard_instruction")}
        </ExperienceFieldGuide>

        <ExperienceFieldLabel darkMode={darkMode}>
          {t("photocard_image")}
          {!isSeason && " *"}
        </ExperienceFieldLabel>
        <Button
          variant="contained"
          component="label"
          sx={{ ...getPrimaryButtonSx(darkMode), alignSelf: "flex-start" }}
        >
          {formik.values.url
            ? t("replace_image")
            : t("upload_image")}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleImageUpload(e, "url")}
          />
        </Button>
        {formik.values.url && (
          <ExperienceMediaCard darkMode={darkMode}>
            <Box
              component="img"
              src={mainImagePreviewUrl}
              alt={t("postcard_preview_alt")}
              sx={{ width: "100%", maxWidth: 360, display: "block" }}
            />
          </ExperienceMediaCard>
        )}
        {formik.touched.url && formik.errors.url && (
          <ExperienceErrorText darkMode={darkMode}>
            {String(formik.errors.url)}
          </ExperienceErrorText>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          label={
            <>
              {isSeason ? t("postcard_note_label_season") : t("postcard_note_label")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          placeholder={t("postcard_note_placeholder")}
          value={formik.values.description}
          inputProps={{ maxLength: 140 }}
          onChange={(e) => {
            const nextValue = e.target.value.slice(0, 140);
            formik.setFieldValue("description", nextValue);
            formik.setFieldError("description", undefined);
          }}
          onBlur={() => formik.setFieldTouched("description", true)}
          error={formik.touched.description && !!formik.errors.description}
          helperText={fieldHelperText(
            formik.touched.description && formik.errors.description
              ? String(formik.errors.description)
              : undefined,
            t("postcard_character_count", {
              count: (formik.values.description || "").length,
            }),
          )}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          label={
            <>
              {isSeason ? t("postcard_title_label_season") : t("postcard_title_label")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          placeholder={t("postcard_title_placeholder")}
          value={formik.values.title}
          onChange={(e) => {
            formik.setFieldValue("title", e.target.value);
            formik.setFieldError("title", undefined);
          }}
          onBlur={() => formik.setFieldTouched("title", true)}
          error={formik.touched.title && !!formik.errors.title}
          helperText={fieldHelperText(
            formik.touched.title && formik.errors.title
              ? String(formik.errors.title)
              : undefined,
            isSeason ? t("postcard_title_helper_season") : t("postcard_title_helper"),
          )}
          sx={fieldSx}
        />

        <ExperienceFieldLabel darkMode={darkMode}>
          {t("signature_image")}
          {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
        </ExperienceFieldLabel>
        <Button
          variant="outlined"
          component="label"
          sx={{ alignSelf: "flex-start", borderRadius: "999px", textTransform: "none" }}
        >
          {formik.values.signature
            ? t("replace_signature")
            : t("upload_signature")}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleImageUpload(e, "signature")}
          />
        </Button>
        {formik.values.signature && (
          <ExperienceMediaCard darkMode={darkMode}>
            <Box
              component="img"
              src={signaturePreviewUrl}
              alt={t("signature_preview_alt")}
              sx={{ width: "100%", maxWidth: 240, display: "block" }}
            />
          </ExperienceMediaCard>
        )}
      </ExperienceShell>
    </FormikProvider>
  );
};

export default PostcardExperience;
